/**
 * "Did you mean …?" for email typos (owner 2026-10-13, after a signup-iPad
 * customer typed kylaslaven@hotmail.con and her emails could never arrive).
 *
 * Pure, no React: the Worker's vitest suite tests it directly. Returns the
 * corrected address, or null when the address looks fine or the fix would be
 * a guess. Two kinds of fix:
 *   1. an ending that is never right (.con, .cmo, .ocm, ...) becomes .com;
 *   2. a near-miss of a common provider (gmial.com, hotmial.com, outlok.com)
 *      becomes that provider, but only when exactly one provider is that close,
 *      and never for a domain that is itself on the list (mail.com, ymail.com).
 */

/** Domains customers actually use, correct as typed. */
const KNOWN_DOMAINS = [
  'gmail.com', 'googlemail.com', 'hotmail.com', 'hotmail.com.au', 'outlook.com',
  'outlook.com.au', 'live.com', 'live.com.au', 'msn.com', 'yahoo.com', 'yahoo.com.au',
  'ymail.com', 'icloud.com', 'me.com', 'mac.com', 'aol.com', 'mail.com', 'gmx.com',
  'protonmail.com', 'proton.me', 'bigpond.com', 'bigpond.net.au', 'optusnet.com.au',
  'tpg.com.au', 'iinet.net.au', 'internode.on.net', 'westnet.com.au', 'outlook.co.nz',
];

/** Endings that are never valid and always mean ".com". */
const BAD_COM_ENDINGS = ['.con', '.cmo', '.ocm', '.vom', '.xom', '.comm', '.c0m', '.cpm', '.coom'];

/** Optimal string alignment distance: insert, delete, substitute, swap neighbours. */
function distance(a: string, b: string): number {
  const d: number[][] = Array.from({ length: a.length + 1 }, (_, i) =>
    Array.from({ length: b.length + 1 }, (_, j) => (i === 0 ? j : j === 0 ? i : 0)));
  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + cost);
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1);
      }
    }
  }
  return d[a.length][b.length];
}

export function suggestEmail(value: string): string | null {
  const email = value.trim();
  const at = email.lastIndexOf('@');
  if (at < 1 || at === email.length - 1 || email.indexOf('@') !== at) return null;
  const local = email.slice(0, at);
  let domain = email.slice(at + 1).toLowerCase();
  if (!domain.includes('.')) return null;

  let changed = false;
  for (const bad of BAD_COM_ENDINGS) {
    if (domain.endsWith(bad)) {
      domain = domain.slice(0, -bad.length) + '.com';
      changed = true;
      break;
    }
  }

  if (!KNOWN_DOMAINS.includes(domain)) {
    // Short domains get less leeway: one slip in "aol.com" is a different word.
    const limit = domain.length >= 9 ? 2 : 1;
    let best: string | null = null;
    let bestDist = Infinity;
    let tie = false;
    for (const known of KNOWN_DOMAINS) {
      const dist = distance(domain, known);
      if (dist < bestDist) {
        best = known;
        bestDist = dist;
        tie = false;
      } else if (dist === bestDist) {
        tie = true;
      }
    }
    if (best && bestDist > 0 && bestDist <= limit && !tie) {
      domain = best;
      changed = true;
    }
  }

  if (!changed) return null;
  const fixed = `${local}@${domain}`;
  return fixed.toLowerCase() === email.toLowerCase() ? null : fixed;
}
